import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { OaktreeLogo } from '../components/OaktreeLogo';
import { Lock, Mail, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { OtpVerifyDialog } from '../components/OtpVerifyDialog';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  const { login, acceptInvite } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // iter 156 — Two-Factor Authentication state. Populated by the backend
  // when the credentials are valid AND MFA is enforced for this account.
  const [otpChallenge, setOtpChallenge] = useState(null);  // {challenge_id, expires_at, cooldown_seconds}

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (inviteToken) {
        await acceptInvite(inviteToken, formData.password);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        const result = await login(formData.email, formData.password);
        if (result?.requiresOtp) {
          setOtpChallenge({
            challenge_id: result.challenge_id,
            expires_at: result.expires_at,
            cooldown_seconds: result.cooldown_seconds || 60,
          });
          toast.success(result.message || `Code sent to ${formData.email}`);
        } else {
          toast.success('Welcome back!');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerified = () => {
    navigate('/dashboard');
  };

  const handleOtpCancel = () => {
    setOtpChallenge(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E3A8A] to-[#2563EB] flex flex-col items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center mb-4">
            <OaktreeLogo size={64} />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
            OAKTREE Recruit
          </h1>
          <p className="text-blue-200">Streamline your recruitment process</p>
        </div>

        <Card className="shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-semibold text-center text-[#1F2937]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {inviteToken ? 'Accept Invitation' : 'Welcome Back'}
            </CardTitle>
            <CardDescription className="text-center">
              {inviteToken 
                ? 'Set your password to complete registration' 
                : 'Sign in to continue to your dashboard'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {!inviteToken && (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1F2937]">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      data-testid="email-input"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1F2937]">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="password-input"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#1E3A8A] hover:bg-[#1e40af] h-11 text-base font-medium"
                disabled={isLoading}
                data-testid="submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {inviteToken ? 'Complete Registration' : 'Sign In'}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-blue-200">
          This is an invite-only system. Contact your administrator for access.
        </p>
      </div>

      {/* iter 156 — Two-Factor verification overlay */}
      <OtpVerifyDialog
        open={!!otpChallenge}
        email={formData.email}
        challengeId={otpChallenge?.challenge_id}
        expiresAt={otpChallenge?.expires_at}
        cooldownSeconds={otpChallenge?.cooldown_seconds}
        onVerified={handleOtpVerified}
        onCancel={handleOtpCancel}
      />
    </div>
  );
}
